import React from 'react';
import {
    DataGridPro,
    DataGridProProps,
    GridColDef,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Skeleton } from '@mui/material';
import { ColumnManagement } from '../../hooks/useTableColumnConfig';
import { ColumnManagementContext } from './columnManagementContext';
import SortableColumnsManagement from './SortableColumnsManagement';

export interface TableDataEntry {
    [key: string]: any
}

interface Props extends DataGridProProps {
    data: TableDataEntry[]
    // When set (see useTableColumnConfig), the columns panel lists the columns in
    // table order and reorders them by drag and drop, header drag writes the same
    // order, and Reset restores the default order and visibility.
    columnManagement?: ColumnManagement
    // When set, preference panels (columns management) open anchored to this
    // element (e.g. the ManageColumnsButton) instead of the grid's top left.
    panelAnchorRef?: React.RefObject<HTMLElement | null>
}

const Table = ({ data, sx, apiRef, slots, slotProps, columnManagement, panelAnchorRef, rowCount, onColumnOrderChange, ...rest }: Props) => {
    const internalApiRef = useGridApiRef();
    const gridApiRef = apiRef ?? internalApiRef;

    // Server-paginated callers pass rowCount={data?.…?.total}, which is
    // undefined until the query resolves — MUI then renders "1–NaN of
    // undefined" in the footer. Remember the last known count so page-change
    // refetches don't flash, and while no count was ever known pass MUI's
    // "unknown count" sentinel (-1) with a skeleton label instead.
    // Client-mode grids must not receive a rowCount key at all — MUI merges
    // defaults with Object.assign, so even an explicit undefined would stick.
    const serverPagination = rest.paginationMode === 'server';
    const lastRowCountRef = React.useRef<number | undefined>(undefined);
    // A 0 count while a request is in flight is never trustworthy — callers that default
    // their total to 0 instead of leaving it undefined would otherwise make MUI clamp the
    // page back to 0 mid-refetch (getPageCount(0, …) === 0).
    const staleCount = serverPagination && !!rest.loading && rowCount === 0 && lastRowCountRef.current != null;
    if (rowCount != null && !staleCount) lastRowCountRef.current = rowCount;
    const resolvedRowCount = staleCount ? lastRowCountRef.current : (rowCount ?? lastRowCountRef.current);
    const rowCountPending = serverPagination && resolvedRowCount == null;

    // Header drag has moved the column in the grid already; hand the full new order
    // (hidden columns included) to the column management.
    const handleColumnOrderChange: DataGridProProps['onColumnOrderChange'] = (params, event, details) => {
        columnManagement?.onOrderChange(gridApiRef.current.getAllColumns().map((col) => col.field));
        onColumnOrderChange?.(params, event, details);
    };

    return (
        <ColumnManagementContext.Provider value={columnManagement ?? null}>
            <DataGridPro
                rows={data}
                apiRef={gridApiRef}
                {...(serverPagination && { rowCount: resolvedRowCount ?? -1 })}
                onColumnOrderChange={handleColumnOrderChange}
                slots={{
                    ...(columnManagement && { columnsManagement: SortableColumnsManagement }),
                    ...slots,
                }}
                slotProps={{
                    ...slotProps,
                    ...(!columnManagement && {
                        // Non-hideable columns would show up as disabled rows in MUI's own
                        // columns panel — leave them out entirely.
                        columnsManagement: {
                            getTogglableColumns: (cols: GridColDef[]) =>
                                cols.filter((col) => col.hideable !== false).map((col) => col.field),
                            ...slotProps?.columnsManagement,
                        },
                    }),
                    ...(panelAnchorRef && {
                        panel: {
                            ...slotProps?.panel,
                            anchorEl: () => panelAnchorRef.current as HTMLElement,
                            placement: 'bottom-end' as const,
                        },
                    }),
                    ...(rowCountPending && {
                        pagination: {
                            labelDisplayedRows: () => (
                                <Skeleton variant="text" width={80} sx={{ display: 'inline-block' }} />
                            ),
                            // count === -1 makes MUI enable the next arrow
                            // ("unknown total") — keep it disabled until the real
                            // count is known.
                            nextIconButtonProps: { disabled: true },
                            ...slotProps?.pagination,
                        },
                    }),
                }}
                sx={[
                    {
                        '&, [class^=MuiDataGrid]': {
                            borderLeft: 'none',
                            borderRight: 'none',
                            borderBottom: 'none',
                            borderRadius: 0,
                        },

                        "& .MuiDataGrid-columnHeader:focus,  .MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        [`& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within`]: {
                            outline: 'none',
                        },

                        '& .MuiDataGrid-footerContainer': {
                            borderTop: 'none',
                        },

                        fontFamily: "Noto Sans",

                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontSize: "14px",
                            lineHeight: "19px",
                            fontWeight: 700,
                        }
                    },
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
                {...rest}
            />
        </ColumnManagementContext.Provider>
    );
}

export default Table;
